import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Trash2, Plus, Eye, EyeOff, Star, BookOpen, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// List of all eBook pages for quick selection
const EBOOK_PAGES = [
  { page: 5, title: 'Needs List' },
  { page: 6, title: 'Feelings List' },
  { page: 7, title: 'Body Sensations' },
  { page: 8, title: 'Your Presence is the Most Precious Gift' },
  { page: 15, title: 'Basic Feelings and Needs' },
  { page: 27, title: 'Non-feeling Words' },
  { page: 29, title: 'Working with Conflict (Short)' },
  { page: 31, title: 'Working with Conflict (Long)' },
  { page: 79, title: 'Chapter 1: Observation and Feelings' },
  { page: 87, title: 'Jackal to OFNR Practice' },
  { page: 88, title: 'Street Giraffe Examples' },
  { page: 90, title: 'OFNR Exercises 1-3' },
  { page: 93, title: 'Chapter 1 End' },
  { page: 94, title: 'Chapter 2: Empathy' },
  { page: 101, title: 'Chapter 3: OFNR Deep Dive' },
  { page: 110, title: 'Chapter 4: Requests' },
  { page: 119, title: 'Homework Partner Schedule' },
];

export default function EbookHighlightManager() {
  const [highlights, setHighlights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newHighlight, setNewHighlight] = useState({
    page: '',
    title: '',
    session_name: '',
    highlight_type: 'featured'
  });
  const [showAddForm, setShowAddForm] = useState(false);

  useEffect(() => {
    fetchHighlights();
  }, []);

  const fetchHighlights = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/ebook/highlights/all`);
      if (response.ok) {
        const data = await response.json();
        setHighlights(data);
      }
    } catch (error) {
      console.error('Error fetching highlights:', error);
      toast.error('Failed to load highlights');
    } finally {
      setLoading(false);
    }
  };

  const handleQuickAdd = (pageInfo) => {
    setNewHighlight({
      ...newHighlight,
      page: pageInfo.page.toString(),
      title: pageInfo.title
    });
    setShowAddForm(true);
  };

  const handleAddHighlight = async () => {
    if (!newHighlight.page || !newHighlight.title) {
      toast.error('Please fill in page and title');
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/ebook/highlights`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...newHighlight,
          page: parseInt(newHighlight.page)
        })
      });

      if (response.ok) {
        toast.success('Highlight added');
        setNewHighlight({ page: '', title: '', session_name: '', highlight_type: 'featured' });
        setShowAddForm(false);
        fetchHighlights();
      } else {
        toast.error('Failed to add highlight');
      }
    } catch (error) {
      console.error('Error adding highlight:', error);
      toast.error('Failed to add highlight');
    }
  };

  const handleToggle = async (id) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/ebook/highlights/toggle/${id}`, {
        method: 'POST'
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(data.active ? 'Highlight activated' : 'Highlight deactivated');
        fetchHighlights();
      }
    } catch (error) {
      console.error('Error toggling highlight:', error);
      toast.error('Failed to toggle highlight');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this highlight?')) return;

    try {
      const response = await fetch(`${BACKEND_URL}/api/ebook/highlights/${id}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        toast.success('Highlight deleted');
        fetchHighlights();
      }
    } catch (error) {
      console.error('Error deleting highlight:', error);
      toast.error('Failed to delete highlight');
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'featured': return <Star className="w-4 h-4 text-amber-500" />;
      case 'upcoming': return <BookOpen className="w-4 h-4 text-blue-500" />;
      case 'completed': return <CheckCircle className="w-4 h-4 text-green-500" />;
      default: return <Star className="w-4 h-4 text-amber-500" />;
    }
  };

  const getTypeBadgeClass = (type) => {
    switch (type) {
      case 'featured': return 'bg-amber-100 text-amber-800 border-amber-200';
      case 'upcoming': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'completed': return 'bg-green-100 text-green-800 border-green-200';
      default: return 'bg-slate-100 text-slate-800 border-slate-200';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-800">eBook Highlight Manager</h2>
          <p className="text-sm text-slate-500">Highlight content to feature for the current session</p>
        </div>
        <Button
          onClick={() => setShowAddForm(!showAddForm)}
          className="bg-emerald-600 hover:bg-emerald-700 text-white"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Highlight
        </Button>
      </div>

      {/* Quick Add Buttons */}
      <Card className="border-slate-200">
        <CardHeader className="py-3">
          <CardTitle className="text-sm font-medium text-slate-600">Quick Add Pages</CardTitle>
        </CardHeader>
        <CardContent className="py-2">
          <div className="flex flex-wrap gap-2">
            {EBOOK_PAGES.map((pageInfo) => (
              <Button
                key={pageInfo.page}
                variant="outline"
                size="sm"
                onClick={() => handleQuickAdd(pageInfo)}
                className="text-xs border-slate-300 hover:border-emerald-500 hover:text-emerald-700"
              >
                p.{pageInfo.page}
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Add Form */}
      {showAddForm && (
        <Card className="border-emerald-200 bg-emerald-50/50">
          <CardHeader className="py-3">
            <CardTitle className="text-sm font-medium text-emerald-800">New Highlight</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="text-xs font-medium text-slate-600">Page Number</label>
                <Input
                  type="number"
                  value={newHighlight.page}
                  onChange={(e) => setNewHighlight({...newHighlight, page: e.target.value})}
                  placeholder="e.g., 87"
                  className="mt-1"
                />
              </div>
              <div className="md:col-span-2">
                <label className="text-xs font-medium text-slate-600">Title</label>
                <Input
                  value={newHighlight.title}
                  onChange={(e) => setNewHighlight({...newHighlight, title: e.target.value})}
                  placeholder="e.g., Jackal to OFNR Practice"
                  className="mt-1"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">Type</label>
                <Select
                  value={newHighlight.highlight_type}
                  onValueChange={(value) => setNewHighlight({...newHighlight, highlight_type: value})}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="featured">⭐ Featured</SelectItem>
                    <SelectItem value="upcoming">📖 Upcoming</SelectItem>
                    <SelectItem value="completed">✅ Completed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Session Name (optional)</label>
              <Input
                value={newHighlight.session_name}
                onChange={(e) => setNewHighlight({...newHighlight, session_name: e.target.value})}
                placeholder="e.g., Week 3 - Empathy"
                className="mt-1"
              />
            </div>
            <div className="flex gap-2">
              <Button onClick={handleAddHighlight} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                Add Highlight
              </Button>
              <Button variant="outline" onClick={() => setShowAddForm(false)}>
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Active Highlights */}
      <Card>
        <CardHeader className="py-3 border-b">
          <CardTitle className="text-sm font-medium text-slate-600 flex items-center gap-2">
            <Eye className="w-4 h-4 text-emerald-500" />
            Active Highlights ({highlights.filter(h => h.active).length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {highlights.filter(h => h.active).length === 0 ? (
            <div className="py-8 text-center text-slate-400">
              No active highlights. Add some content to feature for students.
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {highlights.filter(h => h.active).map((highlight) => (
                <div key={highlight._id} className="p-4 flex items-center justify-between hover:bg-slate-50">
                  <div className="flex items-center gap-4">
                    <span className="w-12 h-12 rounded-lg bg-emerald-100 flex items-center justify-center text-emerald-700 font-bold">
                      {highlight.page}
                    </span>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-slate-800">{highlight.title}</span>
                        <span className={`px-2 py-0.5 rounded-full text-xs border ${getTypeBadgeClass(highlight.highlight_type)}`}>
                          {getTypeIcon(highlight.highlight_type)}
                        </span>
                      </div>
                      {highlight.session_name && (
                        <p className="text-xs text-slate-500 mt-1">{highlight.session_name}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleToggle(highlight._id)}
                      className="border-slate-300"
                    >
                      <EyeOff className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDelete(highlight._id)}
                      className="border-red-200 text-red-600 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Inactive Highlights */}
      {highlights.filter(h => !h.active).length > 0 && (
        <Card className="opacity-75">
          <CardHeader className="py-3 border-b">
            <CardTitle className="text-sm font-medium text-slate-500 flex items-center gap-2">
              <EyeOff className="w-4 h-4" />
              Inactive Highlights ({highlights.filter(h => !h.active).length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y divide-slate-100">
              {highlights.filter(h => !h.active).map((highlight) => (
                <div key={highlight._id} className="p-4 flex items-center justify-between hover:bg-slate-50">
                  <div className="flex items-center gap-4">
                    <span className="w-12 h-12 rounded-lg bg-slate-100 flex items-center justify-center text-slate-400 font-bold">
                      {highlight.page}
                    </span>
                    <div>
                      <span className="font-medium text-slate-500">{highlight.title}</span>
                      {highlight.session_name && (
                        <p className="text-xs text-slate-400 mt-1">{highlight.session_name}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleToggle(highlight._id)}
                      className="border-emerald-200 text-emerald-600 hover:bg-emerald-50"
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDelete(highlight._id)}
                      className="border-red-200 text-red-600 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
